import React, { useEffect, useMemo, useState } from 'react';
import './Reservations.css';
import { getAllServices } from '../../services/serviceService';
import { useNavigate } from 'react-router-dom';
import { getOperators } from '../../services/operatorService';
import ServicesFiltersModal from '../../components/modals/ServicesFiltersModal';
import Table from '../../components/common/Table';
import { formatDate } from '../../utils/date';
import '../../components/common/IconButton.css';
import { IconArrowRight, IconFilter } from '../../components/common/Icons';

const ServicesPage = () => {
  const DEFAULT_STATUS_FILTERS = ['Completo', 'Incompleto'];
  const [services, setServices] = useState([]);
  const [search, setSearch] = useState('');
  const [operators, setOperators] = useState([]);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilters, setStatusFilters] = useState(DEFAULT_STATUS_FILTERS);
  const [typeFilter, setTypeFilter] = useState('');
  const [operatorIdFilter, setOperatorIdFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const navigate = useNavigate();

  const loadData = async () => {
    const svc = await getAllServices();
    setServices(svc);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    getOperators().then(setOperators).catch((err) => console.error(err));
  }, []);

  const serviceTypes = useMemo(() => {
    return Array.from(
      new Set((services || []).map((s) => (s.type || '').trim()).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
  }, [services]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (services || []).filter((s) => {
      const date = s.date_in?.slice(0, 10) || '';
      const status = s.status || 'Incompleto';

      if (dateFrom && (!date || date < dateFrom)) return false;
      if (dateTo && (!date || date > dateTo)) return false;
      if (typeFilter && (s.type || '') !== typeFilter) return false;
      if (!statusFilters.includes(status)) return false;
      if (operatorIdFilter && String(s.operator_id) !== String(operatorIdFilter)) return false;
      if (currencyFilter && s.currency !== currencyFilter) return false;

      return `${s.file_id || ''} ${s.file_number || ''} ${s.client_surname || ''} ${s.client_name || ''} ${s.operator_name || ''} ${s.type || ''}`
        .toLowerCase()
        .includes(q);
    });
  }, [services, search, dateFrom, dateTo, typeFilter, statusFilters, operatorIdFilter, currencyFilter]);

  const columns = [
    {
      id: 'file',
      header: '# Ficha',
      accessor: (s) => s.file_id ?? s.file_number ?? '-',
      sortAccessor: (s) => Number(s.file_id ?? s.file_number ?? 0)
    },
    {
      id: 'client',
      header: 'Cliente',
      accessor: (s) => `${s.client_surname || ''}, ${s.client_name || ''}`.replace(/^,\s*$/, '-'),
      sortAccessor: (s) => `${s.client_surname || ''} ${s.client_name || ''}`.trim()
    },
    {
      id: 'operator',
      header: 'Operador',
      accessor: (s) => s.operator_name || '-',
      sortAccessor: (s) => s.operator_name || ''
    },
    {
      id: 'type',
      header: 'Tipo',
      accessor: (s) => s.type || '-',
      sortAccessor: (s) => s.type || ''
    },
    {
      id: 'date_in',
      header: 'Fecha de salida',
      accessor: (s) => formatDate(s.date_in),
      sortAccessor: (s) => s.date_in || ''
    },
    {
      id: 'status',
      header: 'Estado',
      cell: (s) => (
        <span
          className={`tag status-tag ${
            s.status === 'Completo'
              ? 'status-completo'
              : s.status === 'Deprecado'
                ? 'status-deprecado'
                : 'status-incompleto'
          }`}
        >
          {(s.status || 'Incompleto').toUpperCase()}
        </span>
      ),
      sortAccessor: (s) => s.status || 'Incompleto'
    },
    {
      id: 'details',
      header: 'Acciones',
      headerClassName: 'th-center',
      sortable: false,
      cell: (s) => (
        <div className="cell-center">
          <button
            type="button"
            className="icon-btn icon-btn--select"
            onClick={() => navigate(`/backoffice/servicio/${s.id}`)}
            aria-label="Ver servicio"
            title="Ver servicio"
          >
            <IconArrowRight />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <div className="actions-row">
          <h1>Servicios</h1>
          <button
            type="button"
            className="icon-btn icon-btn--edit"
            onClick={() => setFiltersOpen(true)}
            aria-label="Filtrar servicios"
            title="Filtrar"
          >
            <IconFilter />
          </button>
        </div>
        <div className="actions-row">
          <input
            className="search-input"
            placeholder="Buscar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <Table
        data={filtered}
        columns={columns}
        rowKey="id"
        className="table"
        emptyMessage="No hay servicios para mostrar."
      />

      <ServicesFiltersModal
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        operators={operators}
        types={serviceTypes}
        initialDateFrom={dateFrom}
        initialDateTo={dateTo}
        initialType={typeFilter}
        initialStatuses={statusFilters}
        initialOperatorId={operatorIdFilter}
        initialCurrency={currencyFilter}
        onApply={({ dateFrom: df, dateTo: dt, type, statuses, operatorId, currency }) => {
          setDateFrom(df || '');
          setDateTo(dt || '');
          setTypeFilter(type || '');
          setStatusFilters(Array.isArray(statuses) && statuses.length ? statuses : []);
          setOperatorIdFilter(operatorId || '');
          setCurrencyFilter(currency || '');
          setFiltersOpen(false);
        }}
      />
    </div>
  );
};

export default ServicesPage;
